import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './PrivacySecurity.css';

const PrivacySecurity = () => {
  const { t } = useTranslation('privacySecurity');
  const { t: tNav } = useTranslation('navigation');
  const [activeTab, setActiveTab] = useState('account-security');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Sample data for demonstration
  const [securitySettings] = useState({
    passwordPolicy: {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: true,
      expirationDays: 90,
      preventReuse: 5
    },
    twoFactorAuth: {
      enabled: true,
      requiredForAdmins: true,
      requiredForTeachers: true,
      requiredForStudents: false,
      backupCodes: true
    },
    sessionManagement: {
      autoLogoutMinutes: 15,
      maxConcurrentSessions: 3,
      rememberMeDays: 30
    },
    ipRestrictions: {
      enabled: false,
      allowedIPs: ['192.168.1.0/24', '10.0.0.0/8'],
      whitelistMode: true
    }
  });

  const [activeSessions] = useState([
    {
      id: 1,
      userId: 'admin001',
      userName: 'Soufiane Rihani',
      userRole: 'Admin',
      ipAddress: '105.72.53.109',
      deviceInfo: 'Chrome / Windows 10',
      location: 'Algiers, Algeria',
      loginTime: '2025-01-07 10:30:00',
      lastActivity: '2025-01-07 14:25:00',
      status: 'active'
    },
    {
      id: 2,
      userId: 'teacher001',
      userName: 'Fatima Zohra',
      userRole: 'Teacher',
      ipAddress: '105.72.53.110',
      deviceInfo: 'Safari / macOS',
      location: 'Oran, Algeria',
      loginTime: '2025-01-07 09:15:00',
      lastActivity: '2025-01-07 14:20:00',
      status: 'active'
    },
    {
      id: 3,
      userId: 'admin002',
      userName: 'Yassine Benali',
      userRole: 'Admin',
      ipAddress: '105.72.53.111',
      deviceInfo: 'Firefox / Linux',
      location: 'Constantine, Algeria',
      loginTime: '2025-01-07 08:45:00',
      lastActivity: '2025-01-07 12:30:00',
      status: 'inactive'
    }
  ]);

  const [failedLogins] = useState([
    {
      id: 1,
      ipAddress: '105.72.53.112',
      username: 'unknown_user',
      attemptTime: '2025-01-07 14:30:00',
      reason: 'Invalid credentials',
      location: 'Unknown',
      deviceInfo: 'Unknown',
      attempts: 5
    },
    {
      id: 2,
      ipAddress: '105.72.53.113',
      username: 'admin001',
      attemptTime: '2025-01-07 14:25:00',
      reason: 'Wrong password',
      location: 'Algiers, Algeria',
      deviceInfo: 'Chrome / Windows 10',
      attempts: 3
    }
  ]);

  const [userDataRequests] = useState([
    {
      id: 1,
      userId: 'student001',
      userName: 'Ahmed Mansouri',
      userEmail: 'ahmed@example.com',
      requestType: 'Data Download',
      requestDate: '2025-01-07',
      status: 'Pending',
      dataSize: '2.5 MB',
      adminNotes: 'Standard request for course completion'
    },
    {
      id: 2,
      userId: 'teacher002',
      userName: 'Karim Boudiaf',
      userEmail: 'karim@example.com',
      requestType: 'Data Deletion',
      requestDate: '2025-01-06',
      status: 'Approved',
      dataSize: '15.2 MB',
      adminNotes: 'User leaving the platform'
    },
    {
      id: 3,
      userId: 'student003',
      userName: 'Sara Khelifi',
      userEmail: 'sara@example.com',
      requestType: 'Data Rectification',
      requestDate: '2025-01-05',
      status: 'Completed',
      dataSize: 'N/A',
      adminNotes: 'Updated email address'
    }
  ]);

  const [auditLogs] = useState([
    {
      id: 1,
      date: '2025-01-07 14:30:00',
      adminUser: 'Soufiane Rihani (admin)',
      action: 'Edited Teacher Email',
      targetUser: 'Fatima Zohra',
      result: 'Success',
      ipAddress: '105.72.53.109',
      deviceInfo: 'Chrome / Windows 10',
      details: 'Updated email from fatima@old.com to fatima@new.com'
    },
    {
      id: 2,
      date: '2025-01-07 14:25:00',
      adminUser: 'Yassine Benali (admin)',
      action: 'Downloaded User Data',
      targetUser: 'Ahmed Mansouri',
      result: 'Success',
      ipAddress: '105.72.53.111',
      deviceInfo: 'Firefox / Linux',
      details: 'Exported 2.5 MB of user data for legal compliance'
    },
    {
      id: 3,
      date: '2025-01-07 14:20:00',
      adminUser: 'Soufiane Rihani (admin)',
      action: 'Deleted Student Data',
      targetUser: 'Karim Boudiaf',
      result: 'Success',
      ipAddress: '105.72.53.109',
      deviceInfo: 'Chrome / Windows 10',
      details: 'Permanently deleted user account and all associated data'
    },
    {
      id: 4,
      date: '2025-01-07 14:15:00',
      adminUser: 'System',
      action: 'Failed Login Attempt',
      targetUser: 'unknown_user',
      result: 'Failed',
      ipAddress: '105.72.53.112',
      deviceInfo: 'Unknown',
      details: '5 consecutive failed login attempts from same IP'
    }
  ]);

  const [breachAlerts] = useState([
    {
      id: 1,
      type: 'Failed Login Alert',
      severity: 'High',
      description: 'Multiple failed login attempts detected',
      details: '10 failed login attempts from IP 105.72.53.112 in the last 10 minutes',
      timestamp: '2025-01-07 14:30:00',
      status: 'Active',
      actionRequired: true
    },
    {
      id: 2,
      type: 'Suspicious File Upload',
      severity: 'Medium',
      description: 'Potentially malicious file detected',
      details: 'User uploaded file with suspicious extension: .exe disguised as .pdf',
      timestamp: '2025-01-07 14:25:00',
      status: 'Resolved',
      actionRequired: false
    },
    {
      id: 3,
      type: 'Large Data Export',
      severity: 'Low',
      description: 'Unusual data export activity',
      details: 'Admin exported 50MB of user data (larger than usual)',
      timestamp: '2025-01-07 14:20:00',
      status: 'Reviewed',
      actionRequired: false
    }
  ]);

  const [policies] = useState([
    {
      id: 1,
      name: 'Privacy Policy',
      language: 'English',
      version: '2.1',
      lastUpdated: '2025-01-05',
      status: 'Active',
      fileSize: '45 KB',
      downloadCount: 1247
    },
    {
      id: 2,
      name: 'Terms of Service',
      language: 'Arabic',
      version: '1.8',
      lastUpdated: '2025-01-04',
      status: 'Active',
      fileSize: '32 KB',
      downloadCount: 892
    },
    {
      id: 3,
      name: 'Cookie Policy',
      language: 'French',
      version: '1.2',
      lastUpdated: '2025-01-03',
      status: 'Draft',
      fileSize: '18 KB',
      downloadCount: 0
    }
  ]);

  // Define tabs - simple approach like Overview component
  const tabs = [
    { id: 'account-security', label: t('tabs.accountSecurity'), icon: 'security' },
    { id: 'user-data-control', label: t('tabs.userDataControl'), icon: 'storage' },
    { id: 'policy-management', label: t('tabs.policyManagement'), icon: 'description' },
    { id: 'logs-history', label: t('tabs.logsHistory'), icon: 'history' },
    { id: 'breach-alerts', label: t('tabs.breachAlerts'), icon: 'warning' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'account-security':
        return (
          <div>
            <div className="content-header">
              <h3>{t('accountSecurity.title', 'Account Security Settings')}</h3>
              <button className="primary-btn">
                <span className="material-icons">save</span>
                {t('accountSecurity.saveChanges', 'Save Changes')}
              </button>
            </div>

            <div className="search-filter-bar">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('accountSecurity.searchPlaceholder', 'Search sessions by user or IP...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <select className="filter-select">
                <option value="all">{t('accountSecurity.allSessions', 'All Sessions')}</option>
                <option value="active">{t('accountSecurity.passwordPolicy.active', 'Active')}</option>
                <option value="inactive">{t('accountSecurity.twoFactorAuth.disabled', 'Disabled')}</option>
              </select>

              <select className="filter-select">
                <option value="all">{t('accountSecurity.allRoles', 'All Roles')}</option>
                <option value="admin">Admin</option>
                <option value="teacher">Teacher</option>
                <option value="student">Student</option>
              </select>
            </div>

            <div className="security-sections">
              {/* Password Policy */}
              <div className="security-section">
                <div className="section-header">
                  <h4>{t('accountSecurity.passwordPolicy.title')}</h4>
                  <span className="section-status active">{t('accountSecurity.passwordPolicy.active')}</span>
                </div>
                
                <div className="settings-grid">
                  <div className="setting-item">
                    <label>{t('accountSecurity.passwordPolicy.minimumLength')}</label>
                    <input 
                      type="number" 
                      value={securitySettings.passwordPolicy.minLength}
                      min="6"
                      max="20"
                      readOnly
                    />
                  </div>
                  
                  <div className="setting-item">
                    <label>{t('accountSecurity.passwordPolicy.expirationDays')}</label>
                    <input 
                      type="number" 
                      value={securitySettings.passwordPolicy.expirationDays}
                      min="30"
                      max="365"
                      readOnly
                    />
                  </div>
                  
                  <div className="setting-item checkbox">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={securitySettings.passwordPolicy.requireUppercase}
                        readOnly
                      />
                      {t('accountSecurity.passwordPolicy.requireUppercase')}
                    </label>
                  </div>
                  
                  <div className="setting-item checkbox">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={securitySettings.passwordPolicy.requireLowercase}
                        readOnly
                      />
                      {t('accountSecurity.passwordPolicy.requireLowercase')}
                    </label>
                  </div>
                  
                  <div className="setting-item checkbox">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={securitySettings.passwordPolicy.requireNumbers}
                        readOnly
                      />
                      {t('accountSecurity.passwordPolicy.requireNumbers')}
                    </label>
                  </div>
                  
                  <div className="setting-item checkbox">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={securitySettings.passwordPolicy.requireSpecialChars}
                        readOnly
                      />
                      {t('accountSecurity.passwordPolicy.requireSpecialChars')}
                    </label>
                  </div>
                </div>
              </div>

              {/* Two-Factor Authentication */}
              <div className="security-section">
                <div className="section-header">
                  <h4>{t('accountSecurity.twoFactorAuth.title')}</h4>
                  <span className={`section-status ${securitySettings.twoFactorAuth.enabled ? 'active' : 'inactive'}`}>
                    {securitySettings.twoFactorAuth.enabled ? t('accountSecurity.twoFactorAuth.enabled') : t('accountSecurity.twoFactorAuth.disabled')}
                  </span>
                </div>
                
                <div className="settings-grid">
                  <div className="setting-item checkbox">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={securitySettings.twoFactorAuth.enabled}
                        readOnly
                      />
                      {t('accountSecurity.twoFactorAuth.enable2FA')}
                    </label>
                  </div>
                  
                  <div className="setting-item checkbox">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={securitySettings.twoFactorAuth.requiredForAdmins}
                        readOnly
                      />
                      {t('accountSecurity.twoFactorAuth.requiredForAdmins')}
                    </label>
                  </div>
                  
                  <div className="setting-item checkbox">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={securitySettings.twoFactorAuth.requiredForTeachers}
                        readOnly
                      />
                      {t('accountSecurity.twoFactorAuth.requiredForTeachers')}
                    </label>
                  </div>
                  
                  <div className="setting-item checkbox">
                    <label>
                      <input 
                        type="checkbox" 
                        checked={securitySettings.twoFactorAuth.requiredForStudents}
                        readOnly
                      />
                      {t('accountSecurity.twoFactorAuth.requiredForStudents')}
                    </label>
                  </div>
                </div>
              </div>

              {/* Session Management */}
              <div className="security-section">
                <div className="section-header">
                  <h4>{t('accountSecurity.sessionManagement.title')}</h4>
                </div>
                
                <div className="settings-grid">
                  <div className="setting-item">
                    <label>{t('accountSecurity.sessionManagement.autoLogout')}</label>
                    <input 
                      type="number" 
                      value={securitySettings.sessionManagement.autoLogoutMinutes}
                      min="5"
                      max="120"
                      readOnly
                    />
                  </div>
                  
                  <div className="setting-item">
                    <label>{t('accountSecurity.sessionManagement.maxConcurrentSessions')}</label>
                    <input 
                      type="number" 
                      value={securitySettings.sessionManagement.maxConcurrentSessions}
                      min="1"
                      max="10"
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* Active Sessions */}
              <div className="security-section">
                <div className="section-header">
                  <h4>{t('accountSecurity.activeSessions.title')}</h4>
                  <button className="secondary-btn">
                    <span className="material-icons">refresh</span>
                    {t('accountSecurity.activeSessions.refresh')}
                  </button>
                </div>
                
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('accountSecurity.activeSessions.tableHeaders.user')}</th>
                        <th>{t('accountSecurity.activeSessions.tableHeaders.role')}</th>
                        <th>{t('accountSecurity.activeSessions.tableHeaders.ipAddress')}</th>
                        <th>{t('accountSecurity.activeSessions.tableHeaders.device')}</th>
                        <th>{t('accountSecurity.activeSessions.tableHeaders.location')}</th>
                        <th>{t('accountSecurity.activeSessions.tableHeaders.loginTime')}</th>
                        <th>{t('accountSecurity.activeSessions.tableHeaders.lastActivity')}</th>
                        <th>{t('accountSecurity.activeSessions.tableHeaders.status')}</th>
                        <th>{t('accountSecurity.activeSessions.tableHeaders.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeSessions.map((session) => (
                        <tr key={session.id}>
                          <td>{session.userName}</td>
                          <td>
                            <span className={`role-badge ${session.userRole.toLowerCase()}`}>
                              {session.userRole}
                            </span>
                          </td>
                          <td>{session.ipAddress}</td>
                          <td>{session.deviceInfo}</td>
                          <td>{session.location}</td>
                          <td>{session.loginTime}</td>
                          <td>{session.lastActivity}</td>
                          <td>
                            <span className={`status-badge ${session.status}`}>
                              {session.status}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button className="action-btn terminate" title={t('accountSecurity.activeSessions.terminateSession')}>
                                <span className="material-icons">logout</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Failed Login Attempts */}
              <div className="security-section">
                <div className="section-header">
                  <h4>{t('accountSecurity.failedLogins.title')}</h4>
                </div>
                
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('accountSecurity.failedLogins.tableHeaders.ipAddress')}</th>
                        <th>{t('accountSecurity.failedLogins.tableHeaders.username')}</th>
                        <th>{t('accountSecurity.failedLogins.tableHeaders.attemptTime')}</th>
                        <th>{t('accountSecurity.failedLogins.tableHeaders.reason')}</th>
                        <th>{t('accountSecurity.failedLogins.tableHeaders.location')}</th>
                        <th>{t('accountSecurity.failedLogins.tableHeaders.device')}</th>
                        <th>{t('accountSecurity.failedLogins.tableHeaders.attempts')}</th>
                        <th>{t('accountSecurity.failedLogins.tableHeaders.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failedLogins.map((login) => (
                        <tr key={login.id}>
                          <td>{login.ipAddress}</td>
                          <td>{login.username}</td>
                          <td>{login.attemptTime}</td>
                          <td>{login.reason}</td>
                          <td>{login.location}</td>
                          <td>{login.deviceInfo}</td>
                          <td>
                            <span className={`attempts-badge ${login.attempts > 3 ? 'high' : 'normal'}`}>
                              {login.attempts}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button className="action-btn block" title={t('accountSecurity.failedLogins.blockIP')}>
                                <span className="material-icons">block</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        );

      case 'user-data-control':
        return (
          <div>
            <div className="content-header">
              <h3>{t('userDataControl.title')}</h3>
              <button className="primary-btn">
                <span className="material-icons">add</span>
                {t('userDataControl.newRequest')}
              </button>
            </div>

            <div className="search-filter-bar">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('userDataControl.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <select 
                value={selectedFilter} 
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">{t('userDataControl.allRequests')}</option>
                <option value="download">Data Download</option>
                <option value="deletion">Data Deletion</option>
                <option value="rectification">Data Rectification</option>
                <option value="anonymization">Data Anonymization</option>
              </select>

              <select className="filter-select">
                <option value="all">{t('userDataControl.allStatus')}</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('userDataControl.tableHeaders.user')}</th>
                    <th>{t('userDataControl.tableHeaders.email')}</th>
                    <th>{t('userDataControl.tableHeaders.requestType')}</th>
                    <th>{t('userDataControl.tableHeaders.requestDate')}</th>
                    <th>{t('userDataControl.tableHeaders.status')}</th>
                    <th>{t('userDataControl.tableHeaders.dataSize')}</th>
                    <th>{t('userDataControl.tableHeaders.adminNotes')}</th>
                    <th>{t('userDataControl.tableHeaders.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {userDataRequests.map((request) => (
                    <tr key={request.id}>
                      <td>{request.userName}</td>
                      <td>{request.userEmail}</td>
                      <td>
                        <span className={`request-type-badge ${request.requestType.toLowerCase().replace(' ', '-')}`}>
                          {request.requestType}
                        </span>
                      </td>
                      <td>{request.requestDate}</td>
                      <td>
                        <span className={`status-badge ${request.status.toLowerCase()}`}>
                          {request.status}
                        </span>
                      </td>
                      <td>{request.dataSize}</td>
                      <td>{request.adminNotes}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="action-btn view" title={t('userDataControl.actions.viewDetails')}>
                            <span className="material-icons">visibility</span>
                          </button>
                          <button className="action-btn approve" title={t('userDataControl.actions.approve')}>
                            <span className="material-icons">check</span>
                          </button>
                          <button className="action-btn reject" title={t('userDataControl.actions.reject')}>
                            <span className="material-icons">close</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'policy-management':
        return (
          <div>
            <div className="content-header">
              <h3>{t('policyManagement.title')}</h3>
              <button className="primary-btn">
                <span className="material-icons">add</span>
                {t('policyManagement.uploadNewPolicy')}
              </button>
            </div>

            <div className="policies-grid">
              {policies.map((policy) => (
                <div key={policy.id} className="policy-card">
                  <div className="policy-header">
                    <h4>{policy.name}</h4>
                    <span className={`status-badge ${policy.status.toLowerCase()}`}>
                      {policy.status}
                    </span>
                  </div>
                  
                  <div className="policy-details">
                    <div className="detail-row">
                      <span className="label">{t('policyManagement.labels.language')}</span>
                      <span className="value">{policy.language}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">{t('policyManagement.labels.version')}</span>
                      <span className="value">{policy.version}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">{t('policyManagement.labels.lastUpdated')}</span>
                      <span className="value">{policy.lastUpdated}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">{t('policyManagement.labels.fileSize')}</span>
                      <span className="value">{policy.fileSize}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">{t('policyManagement.labels.downloads')}</span>
                      <span className="value">{policy.downloadCount}</span>
                    </div>
                  </div>
                  
                  <div className="policy-actions">
                    <button className="action-btn edit">
                      <span className="material-icons">edit</span>
                      {t('policyManagement.actions.edit')}
                    </button>
                    <button className="action-btn download">
                      <span className="material-icons">download</span>
                      {t('policyManagement.actions.download')}
                    </button>
                    <button className="action-btn preview">
                      <span className="material-icons">preview</span>
                      {t('policyManagement.actions.preview')}
                    </button>
                    <button className="action-btn delete">
                      <span className="material-icons">delete</span>
                      {t('policyManagement.actions.delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="consent-management">
              <h4>{t('policyManagement.consentManagement.title')}</h4>
              <div className="consent-settings">
                <div className="consent-item">
                  <label>
                    <input type="checkbox" defaultChecked />
                    {t('policyManagement.consentManagement.requirePrivacyPolicy')}
                  </label>
                  <p>{t('policyManagement.consentManagement.requirePrivacyPolicyDesc')}</p>
                </div>
                
                <div className="consent-item">
                  <label>
                    <input type="checkbox" defaultChecked />
                    {t('policyManagement.consentManagement.requireTerms')}
                  </label>
                  <p>{t('policyManagement.consentManagement.requireTermsDesc')}</p>
                </div>
                
                <div className="consent-item">
                  <label>
                    <input type="checkbox" />
                    {t('policyManagement.consentManagement.requireCookie')}
                  </label>
                  <p>{t('policyManagement.consentManagement.requireCookieDesc')}</p>
                </div>
                
                <div className="consent-item">
                  <label>
                    <input type="checkbox" />
                    {t('policyManagement.consentManagement.requireMarketing')}
                  </label>
                  <p>{t('policyManagement.consentManagement.requireMarketingDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'logs-history':
        return (
          <div>
            <div className="content-header">
              <h3>{t('logsHistory.title')}</h3>
              <button className="primary-btn">
                <span className="material-icons">download</span>
                {t('logsHistory.exportLogs')}
              </button>
            </div>

            <div className="search-filter-bar">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('logsHistory.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <select 
                value={selectedFilter} 
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">{t('logsHistory.allActions')}</option>
                <option value="edit">Edit Actions</option>
                <option value="delete">Delete Actions</option>
                <option value="download">Download Actions</option>
                <option value="login">Login Actions</option>
              </select>

              <select className="filter-select">
                <option value="all">{t('logsHistory.allResults')}</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('logsHistory.tableHeaders.dateTime')}</th>
                    <th>{t('logsHistory.tableHeaders.adminUser')}</th>
                    <th>{t('logsHistory.tableHeaders.action')}</th>
                    <th>{t('logsHistory.tableHeaders.targetUser')}</th>
                    <th>{t('logsHistory.tableHeaders.result')}</th>
                    <th>{t('logsHistory.tableHeaders.ipAddress')}</th>
                    <th>{t('logsHistory.tableHeaders.device')}</th>
                    <th>{t('logsHistory.tableHeaders.details')}</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => (
                    <tr key={log.id}>
                      <td>{log.date}</td>
                      <td>{log.adminUser}</td>
                      <td>
                        <span className={`action-badge ${log.action.toLowerCase().replace(' ', '-')}`}>
                          {log.action}
                        </span>
                      </td>
                      <td>{log.targetUser}</td>
                      <td>
                        <span className={`result-badge ${log.result.toLowerCase()}`}>
                          {log.result}
                        </span>
                      </td>
                      <td>{log.ipAddress}</td>
                      <td>{log.deviceInfo}</td>
                      <td>
                        <span className="details-tooltip" title={log.details}>
                          {log.details.substring(0, 30)}...
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'breach-alerts':
        return (
          <div>
            <div className="content-header">
              <h3>{t('breachAlerts.title')}</h3>
              <button className="primary-btn">
                <span className="material-icons">settings</span>
                {t('breachAlerts.alertSettings')}
              </button>
            </div>

            <div className="alerts-overview">
              <div className="alert-stats">
                <div className="stat-card high">
                  <span className="stat-number">{breachAlerts.filter(alert => alert.severity === 'High').length}</span>
                  <span className="stat-label">{t('breachAlerts.stats.highPriority')}</span>
                </div>
                <div className="stat-card medium">
                  <span className="stat-number">{breachAlerts.filter(alert => alert.severity === 'Medium').length}</span>
                  <span className="stat-label">{t('breachAlerts.stats.mediumPriority')}</span>
                </div>
                <div className="stat-card low">
                  <span className="stat-number">{breachAlerts.filter(alert => alert.severity === 'Low').length}</span>
                  <span className="stat-label">{t('breachAlerts.stats.lowPriority')}</span>
                </div>
                <div className="stat-card active">
                  <span className="stat-number">{breachAlerts.filter(alert => alert.status === 'Active').length}</span>
                  <span className="stat-label">{t('breachAlerts.stats.activeAlerts')}</span>
                </div>
              </div>
            </div>

            <div className="alerts-list">
              {breachAlerts.map((alert) => (
                <div key={alert.id} className={`alert-card ${alert.severity.toLowerCase()}`}>
                  <div className="alert-header">
                    <div className="alert-info">
                      <h4>{alert.type}</h4>
                      <span className={`severity-badge ${alert.severity.toLowerCase()}`}>
                        {alert.severity}
                      </span>
                    </div>
                    <div className="alert-meta">
                      <span className="timestamp">{alert.timestamp}</span>
                      <span className={`status-badge ${alert.status.toLowerCase()}`}>
                        {alert.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="alert-content">
                    <p className="description">{alert.description}</p>
                    <p className="details">{alert.details}</p>
                  </div>
                  
                  <div className="alert-actions">
                    {alert.actionRequired && (
                      <button className="action-btn urgent">
                        <span className="material-icons">priority_high</span>
                        {t('breachAlerts.actions.actionRequired')}
                      </button>
                    )}
                    <button className="action-btn resolve">
                      <span className="material-icons">check_circle</span>
                      {t('breachAlerts.actions.markResolved')}
                    </button>
                    <button className="action-btn investigate">
                      <span className="material-icons">search</span>
                      {t('breachAlerts.actions.investigate')}
                    </button>
                    <button className="action-btn dismiss">
                      <span className="material-icons">close</span>
                      {t('breachAlerts.actions.dismiss')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return <div className="tab-content">Select a tab to view content</div>;
    }
  };

  return (
    <div className="privacy-security-container">
      <h2>{tNav('security')}</h2>
      
      {/* Tabs Navigation */}
      <div className="tabs-navigation">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="material-icons">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default PrivacySecurity; 